import { useEffect, useState } from "react";
import { X, PauseCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * ParkOpportunityModal — captures why a deal is being parked and when to revisit it.
 * The revisit date drives park_reminder_engine.py's daily scan, which creates a
 * to-do task once that date is within its lead window.
 */
export function ParkOpportunityModal({ open, onOpenChange, opportunity, onParked }) {
  const [reason, setReason] = useState("");
  const [revisitDate, setRevisitDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setRevisitDate("");
    }
  }, [open]);

  if (!open) return null;

  async function submit() {
    if (!reason.trim()) { toast.error("Add a reason for parking this deal"); return; }
    if (!revisitDate) { toast.error("Pick a date to revisit"); return; }
    setSubmitting(true);
    try {
      await api.patch(`/opportunities/${opportunity.id}`, {
        status: "parked",
        park_reason: reason.trim(),
        park_revisit_date: new Date(`${revisitDate}T23:59:59`).toISOString(),
      });
      toast.success("Parked");
      onParked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not park this deal");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
      onClick={() => onOpenChange(false)}
      data-testid="park-opportunity-modal"
    >
      <div
        className="w-full max-w-md bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]">
            <PauseCircle size={16} />
          </div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Park this deal?</div>
          <button onClick={() => onOpenChange(false)} data-testid="park-opp-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {opportunity?.company?.name && (
            <p className="text-[12px] text-[#526D7A]">
              Parking <strong className="text-[#243B53]">{opportunity.company.name}</strong> — {opportunity.name}
            </p>
          )}
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Reason for parking *</label>
            <textarea
              rows={3}
              autoFocus
              placeholder="Why is this deal being put on hold?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="park-opp-reason"
              className="mt-1.5 w-full bg-[#F8FCFE] border border-[#D7E8F0] rounded-[10px] px-3 py-2.5 text-[13px] text-[#243B53] resize-none outline-none focus:border-[#07539A] focus:bg-white transition-colors"
            />
          </div>
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Date to revisit *</label>
            <input
              type="date"
              min={todayIso()}
              value={revisitDate}
              onChange={(e) => setRevisitDate(e.target.value)}
              data-testid="park-opp-revisit-date"
              className="mt-1.5 w-full h-10 bg-[#F8FCFE] border border-[#D7E8F0] rounded-[10px] px-3 text-[13px] text-[#243B53] outline-none focus:border-[#07539A] focus:bg-white transition-colors"
            />
            <p className="mt-1.5 text-[10px] text-[#78909C]">You'll get a to-do reminder 3 days before, then on the day itself.</p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#C9DCE6] bg-[#E7F5FB] flex justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="h-10 px-4 rounded-[12px] text-[12px] border border-[#BBD3DF] bg-white text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="park-opp-submit"
            className="h-10 px-5 rounded-[12px] text-[12px] font-medium bg-[#07539A] hover:bg-[#063F73] !text-white disabled:opacity-50 flex items-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)] transition-colors"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <PauseCircle size={12} />}
            Park deal
          </button>
        </div>
      </div>
    </div>
  );
}
